// assets/AllCourses/Exercises/DragAndDropExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function DragAndDropExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.dragAndDropQuestions)
    ? exercise.dragAndDropQuestions
    : [];

  /* ================= STATE ================= */
  const [state, setState] = useState({
    questions: [],
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    const initial = {
      questions: questions.map(q => {
        const blankCount = (q.sentence.match(/___/g) || []).length;
        return {
          availableOptions: [...(q.options || [])].sort(
            () => Math.random() - 0.5
          ),
          droppedAnswers: Array(blankCount).fill(""),
        };
      }),
      hasInteracted: false,
    };

    setState(initial);
    completedRef.current = false;
  }, [exercise._id]);

  useEffect(() => {
    if (!showResults) completedRef.current = false;
  }, [showResults]);

  /* ================= EDIT MODE ================= */
  const updateQuestions = next => {
    if (isEditMode) onUpdate("dragAndDropQuestions", next);
  };

  const updateQuestionField = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || {
      sentence: "",
      options: [],
      answer: [],
      requireCapitalization: false,
    };

    let updated = { ...current, [field]: value };

    if (field === "sentence") {
      const blankCount = (value.match(/___/g) || []).length;
      updated.answer = Array.from(
        { length: blankCount },
        (_, i) => current.answer?.[i] || ""
      );
    }

    next[qIdx] = updated;
    updateQuestions(next);
  };

  /* ================= DROP ================= */
  const handleDrop = (qIdx, blankIdx, option) => {
    if (!isPreview || showResults || !option) return;

    setState(prev => {
      const next = structuredClone(prev);
      const qState = next.questions[qIdx];

      if (!qState || !qState.availableOptions.includes(option)) return prev;

      qState.droppedAnswers[blankIdx] = option;
      qState.availableOptions = qState.availableOptions.filter(
        o => o !== option
      );

      next.hasInteracted = true;

      // ✅ IMPORTANT: update parent on every interaction
      onComplete(next.questions, false);

      const allFilled = next.questions.every(q =>
        q.droppedAnswers.every(Boolean)
      );

      if (allFilled) {
        checkAnswers(next);
      }

      return next;
    });
  };

  /* ================= CHECK ================= */
  const checkAnswers = currentState => {
    const allCorrect = questions.every((q, qIdx) => {
      const userAnswers =
        currentState.questions[qIdx]?.droppedAnswers || [];

      return q.answer.every((ans, aIdx) => {
        const user = String(userAnswers[aIdx] || "").trim();
        const correct = String(ans || "").trim();

        return q.requireCapitalization
          ? user === correct
          : user.toLowerCase() === correct.toLowerCase();
      });
    });

    // ✅ FINAL RESULT SEND
    onComplete(currentState.questions, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => {
          const blankCount =
            (q.sentence.match(/___/g) || []).length;

          return (
            <Card key={qIdx} className="p-6">
              <div className="space-y-6">
                <TextareaField
                  label={`Sentence ${qIdx + 1}`}
                  value={q.sentence || ""}
                  onChange={v =>
                    updateQuestionField(qIdx, "sentence", v)
                  }
                />

                {/* OPTIONS */}
                <div className="space-y-3">
                  {q.options.map((opt, optIdx) => (
                    <div key={optIdx} className="flex gap-3">
                      <StringField
                        value={opt}
                        onChange={v => {
                          const next = [...questions];
                          next[qIdx].options[optIdx] = v;
                          updateQuestions(next);
                        }}
                      />
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          const next = [...questions];
                          next[qIdx].options =
                            next[qIdx].options.filter((_, i) => i !== optIdx);
                          updateQuestions(next);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}

                  <Button
                    size="sm"
                    onClick={() => {
                      const next = [...questions];
                      next[qIdx].options.push("");
                      updateQuestions(next);
                    }}
                  >
                    Add Option
                  </Button>
                </div>

                {/* ANSWERS */}
                <div className="space-y-3">
                  {Array.from({ length: blankCount }).map((_, i) => (
                    <StringField
                      key={i}
                      label={`Blank ${i + 1}`}
                      value={q.answer[i] || ""}
                      onChange={v => {
                        const next = [...questions];
                        next[qIdx].answer[i] = v;
                        updateQuestions(next);
                      }}
                    />
                  ))}
                </div>

                <CapitalizationRule
                  checked={!!q.requireCapitalization}
                  onChange={c =>
                    updateQuestionField(qIdx, "requireCapitalization", c)
                  }
                />
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT ================= */
  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Drag and Drop</CardTitle>
        <CardDescription>
          Drag the words into the correct blanks.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const parts = q.sentence.split("___");
          const qState = state.questions[qIdx] || {
            availableOptions: [],
            droppedAnswers: [],
          };

          return (
            <div key={qIdx} className="space-y-6">
              {/* WORD BANK */}
              <div className="rounded-xl border bg-muted/40 p-4">
                <div className="flex flex-wrap justify-center gap-3 min-h-[64px]">
                  {qState.availableOptions.length ? (
                    qState.availableOptions.map(opt => (
                      <Button
                        key={opt}
                        draggable={!showResults}
                        variant="secondary"
                        size="sm"
                        onDragStart={e =>
                          e.dataTransfer.setData("text/plain", opt)
                        }
                      >
                        {opt}
                      </Button>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      All words used
                    </p>
                  )}
                </div>
              </div>

              {/* SENTENCE */}
              <div className="rounded-xl border p-6 flex flex-wrap gap-3 text-lg">
                {parts.map((part, i) => (
                  <React.Fragment key={i}>
                    {part}

                    {i < parts.length - 1 && (
                      <div
                        onDragOver={e => e.preventDefault()}
                        onDrop={e =>
                          handleDrop(
                            qIdx,
                            i,
                            e.dataTransfer.getData("text/plain")
                          )
                        }
                        className={cn(
                          "min-w-[120px] h-10 border-2 border-dashed flex items-center justify-center",
                          showResults && "opacity-95"
                        )}
                      >
                        {qState.droppedAnswers[i] || "___"}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}